import type { ChromeTitleKeyType, ChromeTranslateFn } from '@featit/shared/chrome-label';
import { workAreaDisplayName } from '@featit/shared/work-area-seed-names';
import { customerAgencyNameOf } from '@featit/shared/customer-sender';
import { isGuestWorkArea } from '@featit/shared/guest-work-area';
import { appPath, parseAddress } from '@featit/shared/addressing/paths';
import {
  workAreaNamespaceAmong,
  workAreaQualifiedLabel,
  workAreaRouteRef,
} from '@featit/shared/addressing/workAreaRouteRef';
import type { IShellOpenWorkAreaSwitcherMessage } from '@featit/shared/shell-bridge';
import { SHELL_ALL_WORK_AREAS_SCOPE, SHELL_WORK_AREA_EDIT_PATH } from '@featit/shared/shell-paths';
import {
  PHONE_SWITCHER_ORDER,
  type IProject,
  type PhoneSwitcherOrderType,
} from '@featit/shared/types/index';
import { workAreaBadgeOf } from '@featit/shared/work-area-badge';
import {
  isNamespaceScopedPath,
  isWorkAreaOverviewPath,
  namespaceIdOfPath,
  WORK_AREAS_ACTIVITY_PATH,
  WORK_AREAS_DECISIONS_PATH,
} from '@/features/work-areas/workAreaOverviewUrls';
import {
  pathForAllWorkAreas,
  pathForWorkAreaSwitch,
  singleWorkAreaViewForPath,
  WORK_AREAS_CHOOSE_PATH,
} from '@/features/work-areas/workAreaScopeRoutes';
import {
  projectActivityPath,
  projectDecisionsPath,
  projectItemsPath,
  projectRoadmapPath,
} from '@/features/work-items/projectItemUrls';
import {
  workAreaSwitcherOrder,
  type SwitcherChoiceWorkAreaType,
} from '@/features/work-areas/workAreaSwitcherOrder';
import { findWorkAreaByRouteRef } from '@/routing/workAreaRoute';
import { hasShellCapability, shellDrawsNavigation } from './shellDetection';
import { hasRouteParent, phoneTabRootOf, type PhoneTabRootType } from './routeParents';

/**
 * F-0530 (`docs/concept-mobile-app.md`, AC-19): the phone's one app-wide work-area scope.
 *
 * ⭐ **One value, the person's choice** (`ProjectStore.appScope`): a work area's id, or
 * `SHELL_ALL_WORK_AREAS_SCOPE`. Every tab root follows it. A tab's address used to decide its scope
 * alone, so Decisions (rooted at `/-/work-areas/decisions`) read "All work areas" while Items read
 * the work area: one app, two scopes. The rules here are pure; `useAppScope` applies them.
 *
 * ⛔ Only in the iPhone app with `workAreaSwitcher`. A browser, the iPad (F-0519) and an older app
 * keep the address-derived scope and the web's own picker sheet unchanged.
 */
export function appScopeSwitcherActive(): boolean {
  return shellDrawsNavigation() && hasShellCapability('workAreaSwitcher');
}

type ScopedWorkAreaType = Pick<IProject, 'id' | 'name' | 'slug' | 'namespace' | 'namespaceKey'>;

/** The tab roots that have a scope: Agents is the account's, whatever the scope. */
type ScopedTabType = Exclude<PhoneTabRootType, 'agents'>;

const ALL_SCOPE_ROOTS: Readonly<Record<ScopedTabType, string>> = {
  decisions: WORK_AREAS_DECISIONS_PATH,
  activity: WORK_AREAS_ACTIVITY_PATH,
  // I-1118 (kept): under All, Items and Roadmap open the last-opened or only work area themselves.
  items: appPath('/items'),
  roadmap: appPath('/roadmap'),
};

const WORK_AREA_ROOTS: Readonly<Record<ScopedTabType, (routeRef: string) => string>> = {
  decisions: projectDecisionsPath,
  activity: projectActivityPath,
  items: projectItemsPath,
  roadmap: projectRoadmapPath,
};

/** The root `tab` opens on under `scope`; `null` while the scope's work area is not known here. */
export function tabRootPathForScope(
  tab: ScopedTabType,
  scope: string,
  projects: readonly ScopedWorkAreaType[],
): string | null {
  if (scope === SHELL_ALL_WORK_AREAS_SCOPE) return ALL_SCOPE_ROOTS[tab];
  const workArea = projects.find((project) => project.id === scope);
  return workArea ? WORK_AREA_ROOTS[tab](workAreaRouteRef(workArea)) : null;
}

/**
 * AC-19.1: where the tab root at `pathname` must go to show `scope`, or `null` to stay. Only a tab
 * root moves; a pushed screen (an item, a decision) stays on its own work area.
 *
 * Under All, Items and Roadmap stay on whichever work area I-1118 opened: those two views need one
 * work area, and the one they show is the honest answer to "which list is this".
 */
export function tabRootFollowTarget(
  pathname: string,
  scope: string | null,
  projects: readonly ScopedWorkAreaType[],
): string | null {
  // I-1362: nothing chosen yet, and one work area, is the line's work area (`appScopeTitleOf` names
  // it): the tab shows that work area too. Left on the all-work-areas feed, the Activity tab drew a
  // list without the chart under a line naming one work area. With several, the line reads All.
  const shown = scope ?? (projects.length === 1 ? projects[0].id : null);
  if (!shown) return null;
  // Activity's tab root is the all-work-areas feed, which `phoneTabRootOf` reads as no tab root (it
  // draws no top row there); the scope still has to move it.
  const tab = pathname === WORK_AREAS_ACTIVITY_PATH ? 'activity' : phoneTabRootOf(pathname);
  if (!tab || tab === 'agents') return null;
  if (shown === SHELL_ALL_WORK_AREAS_SCOPE && (tab === 'items' || tab === 'roadmap')) return null;
  const target = tabRootPathForScope(tab, shown, projects);
  return target && target !== pathname ? target : null;
}

/**
 * RD-24 (I-1477): whether this window's address is a tab root about to move to the app's scope. The
 * scope changes in one commit and the router's navigation lands in a later one, so for a few frames
 * the address still names the scope that was left. Read as the scope there, it moved every badge
 * store back: All → work area → All → work area on one relayed pick (measured). While this holds,
 * the store's scope is the scope, not the address.
 *
 * RD-29 (I-1482): and a screen about a scope that is not a tab root (Tasks for me under All, opened
 * from More), while it follows a choice heard on this address (`followingFrom`, `useAppScope`) by
 * `screenFollowTarget`. Without a choice heard, such an address is its own scope (I-0405).
 */
export function addressFollowsAppScope(
  pathname: string,
  scope: string | null,
  projects: readonly ScopedWorkAreaType[],
  followingFrom: string | null = null,
): boolean {
  if (!appScopeSwitcherActive()) return false;
  if (tabRootFollowTarget(pathname, scope, projects) !== null) return true;
  return followingFrom === pathname && screenFollowTarget(pathname, '', scope, projects) !== null;
}

/**
 * I-1363 (Jay, 2026-10-09: "the work area selector should work app wide, i.e. if changed on one
 * tab or settings it should change everywhere in the mobile app"): where a screen that is about a
 * scope, but is not a tab root, goes when the scope is chosen anywhere — Work area settings, Home,
 * Parts, a work area's Views, an organisation's page, opened from More. `null` means stay.
 *
 * A tab root follows by `tabRootFollowTarget`, on every address; this one follows only a choice
 * (`useAppScope` calls it on `workAreaScope`), so a link into another work area's page is never
 * sent back. It goes where the web's own picker sends a switch (`pathForWorkAreaSwitch`). Under
 * All, a page about every work area takes its all-work-areas twin, and a page that needs one work
 * area (Settings, a page, Items, Roadmap) stays on its own, as Items and Roadmap do (I-1118).
 * A pushed screen (an item, a decision) and an account page (Global settings, Profile) stay:
 * they draw no scope line (`appScopeLineShownAt`).
 */
export function screenFollowTarget(
  pathname: string,
  search: string,
  scope: string | null,
  projects: readonly ScopedWorkAreaType[],
): string | null {
  if (!scope || !appScopeLineShownAt(pathname)) return null;
  if (pathname === WORK_AREAS_ACTIVITY_PATH || phoneTabRootOf(pathname)) return null;
  const addressed = workAreaOfPath(pathname, projects);
  if (scope === SHELL_ALL_WORK_AREAS_SCOPE) {
    if (!addressed && !isNamespaceScopedPath(pathname)) return null;
    if (singleWorkAreaViewForPath(pathname)) return null;
    const target = pathForAllWorkAreas(pathname);
    return target && target !== pathname ? target : null;
  }
  if (addressed?.id === scope) return null;
  const workArea = projects.find((project) => project.id === scope);
  if (!workArea) return null;
  const target = pathForWorkAreaSwitch(pathname, search, workAreaRouteRef(workArea));
  return target && target !== pathname ? target : null;
}

/** The work area an address names, when it names one this person can read. */
export function workAreaOfPath<T extends ScopedWorkAreaType>(
  pathname: string,
  projects: readonly T[],
): T | undefined {
  const address = parseAddress(pathname.split('#', 1)[0].split('?', 1)[0]);
  return address.kind === 'work-area'
    ? findWorkAreaByRouteRef(projects, address.reference)
    : undefined;
}

export interface IAppScopeTitle {
  /** The scope's own name: the work area's, "All work areas", a namespace's. */
  readonly name: string;
  /**
   * AC-19.14 (I-1348): the namespace the scope line names before a work area's name, as "Namespace /
   * Work area", where the name alone is ambiguous (`scopeLineQualifier`); otherwise `null`.
   */
  readonly qualifier: string | null;
  /**
   * AC-19.13 (I-1345): what a picker says, "Namespace / Work area" (`workAreaQualifiedLabel`); the
   * name itself for All work areas, a namespace, or a work area whose namespace is not known yet.
   */
  readonly label: string;
  /** More than one work area: the title opens the switcher and carries ▾ (AC-19.5). */
  readonly choosable: boolean;
  /**
   * I-2362: the key a scope the product names is read under (All work areas), translated where it
   * is drawn (`appScopeTitleIn`); `name` and `label` are its English. A record's name has none.
   */
  readonly i18n?: { readonly name: ChromeTitleKeyType };
}

/** All work areas as a scope's title, read in the reader's language where it is drawn (I-2362). */
function allWorkAreasTitle(choosable: boolean): IAppScopeTitle {
  return {
    name: 'All work areas',
    label: 'All work areas',
    qualifier: null,
    choosable,
    i18n: { name: 'chromeTitles.allWorkAreas' },
  };
}

/** A scope title in the reader's language: the product's own name for a scope through `t`. */
export function appScopeTitleIn(t: ChromeTranslateFn, title: IAppScopeTitle): IAppScopeTitle;
export function appScopeTitleIn(
  t: ChromeTranslateFn,
  title: IAppScopeTitle | null,
): IAppScopeTitle | null;
export function appScopeTitleIn(
  t: ChromeTranslateFn,
  title: IAppScopeTitle | null,
): IAppScopeTitle | null {
  if (!title?.i18n) return title;
  const name = t(title.i18n.name);
  return { ...title, name, label: name };
}

type TitleWorkAreaType = ScopedWorkAreaType &
  Pick<IProject, 'namespaceName'> &
  Partial<Pick<IProject, 'nameSeedId' | 'namespaceIsOwn'>> & {
    /** PX-33: a guest's work area is named by its agency (`guestTitleOf`). */
    access?: Pick<NonNullable<IProject['access']>, 'guest'> | null;
  };

/**
 * AC-19.14 (I-1348; Jay, 2026-10-09: "generally dont show avatar icons on mobile"): the scope line
 * names a work area in words, with no badge. The name alone, unless another of the reader's work
 * areas has the same name in another namespace: then its namespace before it, "Namespace / Work
 * area" as `workAreaQualifiedLabel` writes it, the one thing that tells the two apart now that no
 * colour does. `null` when the name is the reader's only one of that name.
 */
export function scopeLineQualifier(
  workArea: TitleWorkAreaType,
  projects: readonly TitleWorkAreaType[],
): string | null {
  // The one rule every list naming work areas follows (the Agents view's too, AC-TB17).
  return workAreaNamespaceAmong(workArea, projects);
}

/** What the scope line says, whole: "Namespace / Work area" with a qualifier, the name without. */
export function scopeLineText(title: Pick<IAppScopeTitle, 'name' | 'qualifier'>): string {
  return title.qualifier ? `${title.qualifier} / ${title.name}` : title.name;
}

/**
 * PX-33 (I-2384, point 3): a customer's scope line and top row name the agency, by the organisation
 * the work area lies in, never the name the agency gave the work area; with no agency to name, whose
 * pages these are, in the reader's language.
 */
function guestTitleOf(workArea: TitleWorkAreaType, choosable: boolean): IAppScopeTitle {
  const agency = customerAgencyNameOf({
    organisation: workArea.namespaceIsOwn ? null : workArea.namespaceName,
  });
  if (agency) return { name: agency, label: agency, qualifier: null, choosable };
  return {
    name: 'Your pages',
    label: 'Your pages',
    qualifier: null,
    choosable,
    i18n: { name: 'chromeTitles.guestPages' },
  };
}

function titleOfWorkArea(
  workArea: TitleWorkAreaType,
  projects: readonly TitleWorkAreaType[],
  choosable: boolean,
): IAppScopeTitle {
  if (isGuestWorkArea(workArea)) return guestTitleOf(workArea, choosable);
  return {
    // F-0737 PW-R3: a Personal work area in the page's language until its person renames it.
    name: workAreaDisplayName(workArea),
    qualifier: scopeLineQualifier(workArea, projects),
    label: workAreaQualifiedLabel(workArea),
    choosable,
  };
}

/**
 * AC-19.2: what the tab root's title says. The app scope's name, the same on every tab; for Items
 * and Roadmap under All, the work area the list is (I-1118). `null` while there is nothing to name
 * yet (no scope chosen and no work area known), so the row never names a guess.
 */
export function appScopeTitleOf(
  pathname: string,
  scope: string | null,
  projects: readonly TitleWorkAreaType[],
  rememberedName?: string,
  /** I-1472: something lies outside the person's work areas. */
  outside = false,
): IAppScopeTitle | null {
  const choosable = scopeChoosable(projects.length, outside);
  const shown =
    scope === SHELL_ALL_WORK_AREAS_SCOPE || scope === null
      ? workAreaOfPath(pathname, projects)
      : projects.find((project) => project.id === scope);
  if (shown) return titleOfWorkArea(shown, projects, choosable);
  // Nothing chosen yet: an all-work-areas address (Decisions' and Activity's roots) is All.
  const allAddress = scope === null && isWorkAreaOverviewPath(pathname);
  if ((scope === SHELL_ALL_WORK_AREAS_SCOPE || allAddress) && choosable) {
    return allWorkAreasTitle(choosable);
  }
  // One work area: its name, whatever was stored before (AC-19.5), unless All is a real choice
  // (I-1472), where the branch above already named it.
  if (projects.length === 1) {
    return titleOfWorkArea(projects[0], projects, false);
  }
  if (scope && rememberedName) {
    return { name: rememberedName, qualifier: null, label: rememberedName, choosable };
  }
  return null;
}

/**
 * AC-19.3: the native sheet's request. The current scope is the app scope, or the work area this
 * window shows when nothing was chosen yet. AC-19.18: the list is in the order the person's setting
 * names, and only Recent first ranks the recent ones the sheet puts above it.
 */
export function workAreaSwitcherMessage(
  scope: string | null,
  shownWorkAreaId: string | null,
  projects: readonly SwitcherChoiceWorkAreaType[],
  recentIds: readonly string[],
  order: PhoneSwitcherOrderType = PHONE_SWITCHER_ORDER.RECENT_THEN_CUSTOM,
  /** I-1472: something lies outside the person's work areas, so the sheet offers All with one. */
  outside = false,
): IShellOpenWorkAreaSwitcherMessage {
  const current = scope ?? shownWorkAreaId ?? SHELL_ALL_WORK_AREAS_SCOPE;
  const { workAreas, ranked } = workAreaSwitcherOrder(order, projects, recentIds, current);
  return {
    type: 'openWorkAreaSwitcher',
    current,
    workAreas: workAreas.map((project) => {
      const rank = ranked.indexOf(project.id);
      return {
        id: project.id,
        name: project.name,
        /*
          AC-19.13 (I-1345): the sheet names every work area as "Namespace / Work area", the reader's
          own namespace included, as every other picker does (`workAreaQualifiedLabel`).
        */
        detail: project.namespaceName?.trim() || project.namespace?.trim() || null,
        ...workAreaBadgeOf(project),
        recentRank: rank === -1 ? null : rank,
      };
    }),
    editPath: SHELL_WORK_AREA_EDIT_PATH,
    ...(outside && projects.length < 2 ? { offerAll: true } : {}),
  };
}

/**
 * Whether there is a scope to choose (AC-19.5, I-1472): more than one work area, or one work area
 * with something outside it, where All work areas is the only way to reach that something.
 */
export function scopeChoosable(projectCount: number, outside: boolean): boolean {
  return projectCount > 1 || (projectCount === 1 && outside);
}

/**
 * F-0530 (AC-19.2, Jay, 2026-10-06): where the phone draws the scope line, the one small line under
 * the page's title. On a root screen about a scope — every tab root, an all-work-areas page,
 * a namespace's page, a work area's own page — and nowhere else: not on a pushed screen (it names
 * its own work area), not on the account's pages (Settings, Profile), not on the choice or the edit
 * view, which are about choosing.
 */
export function appScopeLineShownAt(pathname: string): boolean {
  if (pathname === SHELL_WORK_AREA_EDIT_PATH || pathname.startsWith(`${WORK_AREAS_CHOOSE_PATH}/`)) {
    return false;
  }
  // I-1354 (Jay, 2026-10-09): every tab root, Agents too — its list is narrowed by the scope.
  if (phoneTabRootOf(pathname)) return true;
  if (hasRouteParent(pathname)) return false;
  return (
    isWorkAreaOverviewPath(pathname) ||
    isNamespaceScopedPath(pathname) ||
    parseAddress(pathname).kind === 'work-area'
  );
}

/**
 * I-1354: the scope the line names. The app scope once one is chosen. Before that, Agents' list is
 * narrowed by the window's own selection (`ProjectStore.scopeWorkAreaId`, `null` for every work
 * area) and has no address to name it, so the line names that selection rather than waiting on a
 * choice that may never come. Every other screen names its address or waits.
 */
export function lineScopeOf(
  pathname: string,
  appScope: string | null,
  selectedScopeWorkAreaId: string | null,
): string | null {
  if (appScope !== null || phoneTabRootOf(pathname) !== 'agents') return appScope;
  return selectedScopeWorkAreaId ?? SHELL_ALL_WORK_AREAS_SCOPE;
}

/** A namespace as the scope line needs it. */
interface IScopeLineNamespace {
  readonly id: string;
  readonly name?: string | null;
  readonly fullPath?: string | null;
}

/**
 * AC-19.2: what the scope line says — the scope of the page on screen, by its display name only
 * (never a namespace handle, never an "All work areas" link). The address's own work area or
 * namespace when it names one, "All work areas" on an all-work-areas page, otherwise the app scope.
 * After a tab root follows the app scope (AC-19.1) the two are the same.
 */
export function scopeLineOf(
  pathname: string,
  scope: string | null,
  projects: readonly TitleWorkAreaType[],
  namespaces: readonly IScopeLineNamespace[] | null,
  rememberedName?: string,
  /** I-1472: something lies outside the person's work areas. */
  outside = false,
): IAppScopeTitle | null {
  const choosable = scopeChoosable(projects.length, outside);
  const addressed = workAreaOfPath(pathname, projects);
  if (addressed) return titleOfWorkArea(addressed, projects, choosable);
  if (isNamespaceScopedPath(pathname)) {
    const id = namespaceIdOfPath(pathname);
    const namespace = namespaces?.find((entry) => entry.id === id);
    const name = namespace?.name || namespace?.fullPath;
    if (namespace && name) {
      return { name, qualifier: null, label: name, choosable };
    }
    return null;
  }
  if (isWorkAreaOverviewPath(pathname) && choosable) {
    return allWorkAreasTitle(choosable);
  }
  return appScopeTitleOf(pathname, scope, projects, rememberedName, outside);
}

/** What the scope line draws when `scopeLineOf` has nothing to name (RD-20, RD-21). */
export type EmptyScopeLineType = 'loading' | 'choose' | 'none';

/** The reads the scope line names from, each answered or failed. */
export interface IScopeLineReads {
  workAreasAnswered: boolean;
  workAreasFailed: boolean;
  namespacesAnswered: boolean;
  namespacesFailed: boolean;
}

/**
 * RD-20 (I-1077, `docs/concept-mobile-app.md`): a loading placeholder only while a read the line
 * names from is under way — the work-area list, and the namespaces on a namespace's address. A read
 * that answered with nothing to name is not loading: the placeholder pulsed for ever for a person
 * with no work area, and the launch's mark waited on it (`whenLaunchContentSettles`).
 *
 * RD-21: answered with several to choose from and none named here, the line is the choice itself.
 * Otherwise (no work area, or a failed read the page reports with Try again) it names nothing.
 */
export function emptyScopeLineOf(
  pathname: string,
  reads: IScopeLineReads,
  choosable: boolean,
): EmptyScopeLineType {
  if (!reads.workAreasAnswered && !reads.workAreasFailed) return 'loading';
  if (isNamespaceScopedPath(pathname) && !reads.namespacesAnswered && !reads.namespacesFailed) {
    return 'loading';
  }
  return choosable ? 'choose' : 'none';
}

/**
 * F-0530 (AC-19.8): the one work area the scope line names, for what waits outside it — the
 * address's work area, otherwise the app scope's; `null` for All work areas or a namespace.
 */
export function scopeLineWorkAreaOf(
  pathname: string,
  scope: string | null,
  projects: readonly ScopedWorkAreaType[],
): string | null {
  const addressed = workAreaOfPath(pathname, projects);
  if (addressed) return addressed.id;
  if (isNamespaceScopedPath(pathname) || isWorkAreaOverviewPath(pathname)) return null;
  if (scope && scope !== SHELL_ALL_WORK_AREAS_SCOPE) return scope;
  return projects.length === 1 ? projects[0].id : null;
}
